import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

const QUESTIONS = [
  {
    key: "space",
    q: "How much space is in your bathroom?",
    options: [
      { label: "Compact / small", value: "wall", hint: "Space-saving picks" },
      { label: "Standard", value: "", hint: "Show me everything" },
      { label: "Spacious / family", value: "floor", hint: "Grounded, roomy pieces" },
    ],
  },
  {
    key: "storage",
    q: "How much storage do you need?",
    options: [
      { label: "Minimal", value: "Minimal", hint: "Clean and simple" },
      { label: "Balanced", value: "", hint: "A bit of everything" },
      { label: "Maximum", value: "Storage-Focused", hint: "Drawers & shelves" },
    ],
  },
  {
    key: "look",
    q: "Which look do you prefer?",
    options: [
      { label: "Modern", value: "Modern" },
      { label: "Minimal", value: "Minimal" },
      { label: "Luxury", value: "Luxury" },
      { label: "Fluted", value: "Fluted" },
    ],
  },
];

export const HelpMeChoose = ({ trigger }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ space: null, storage: null, look: null });

  const reset = () => {
    setStep(0);
    setAnswers({ space: null, storage: null, look: null });
  };

  const choose = (key, value) => {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      finish(next);
    }
  };

  const finish = (a) => {
    const params = new URLSearchParams();
    if (a.space) params.set("mount", a.space === "wall" ? "wall" : "floor");
    // Storage "Maximum" wins as the style filter, otherwise the look choice.
    const style = a.storage === "Storage-Focused" ? "Storage-Focused" : a.look;
    if (style) params.set("style", style);
    setOpen(false);
    setTimeout(() => {
      navigate(`/vanities${params.toString() ? `?${params.toString()}` : ""}`);
      reset();
    }, 150);
  };

  const current = QUESTIONS[step];

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        data-testid="quiz-dialog"
        className="bg-ivory border-beige sm:rounded-sm max-w-md p-0 overflow-hidden gap-0"
      >
        <DialogTitle className="sr-only">Help me choose the right vanity</DialogTitle>
        {/* Progress */}
        <div className="px-6 pt-6">
          <div className="flex items-center gap-1.5">
            {QUESTIONS.map((_, i) => (
              <span
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
                  i <= step ? "bg-charcoal" : "bg-beige"
                }`}
              />
            ))}
          </div>
          <p className="eyebrow text-taupe mt-4">
            Help me choose · {step + 1} of {QUESTIONS.length}
          </p>
        </div>

        <div className="px-6 pb-6 pt-3">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              <h2 className="font-serif font-light text-charcoal text-[1.7rem] leading-tight">
                {current.q}
              </h2>

              <div className="mt-6 space-y-2.5">
                {current.options.map((opt) => {
                  const active = answers[current.key] === opt.value;
                  return (
                    <button
                      key={opt.label}
                      onClick={() => choose(current.key, opt.value)}
                      data-testid={`quiz-${current.key}-${opt.label.split(" ")[0].toLowerCase()}`}
                      className={`w-full flex items-center justify-between gap-3 rounded-sm border px-5 py-4 text-left transition-colors duration-200 ${
                        active
                          ? "border-charcoal bg-stone/60"
                          : "border-beige hover:border-charcoal"
                      }`}
                    >
                      <span>
                        <span className="block text-sm font-medium text-charcoal">{opt.label}</span>
                        {opt.hint && <span className="block text-xs text-taupe mt-0.5">{opt.hint}</span>}
                      </span>
                      <span className="grid place-items-center h-7 w-7 rounded-full border border-beige text-charcoal shrink-0">
                        {step === QUESTIONS.length - 1 ? <Check size={13} strokeWidth={2} /> : <ArrowRight size={14} strokeWidth={1.6} />}
                      </span>
                    </button>
                  );
                })}
              </div>

              {step > 0 && (
                <button
                  onClick={() => setStep(step - 1)}
                  data-testid="quiz-back"
                  className="mt-5 inline-flex items-center gap-2 text-xs text-taupe link-sweep"
                >
                  <ArrowLeft size={13} strokeWidth={1.6} /> Back
                </button>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
};
